import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';

import { FormattedNumberTextField } from '../../common/FormattedNumberTextField';
import { LLMModelUnitPriceInput, LLMModelUnitPriceType } from '../../../types/admin';
import { usePricingDimensions } from '../../../services/llm-catalog.service';
import {
  RESOLUTION_DIMENSION_KEY,
  RESOLUTION_UNIT_TYPES,
  UNIT_PRICE_OPTIONS,
} from './modelPricing';
import { findDuplicateUnitPriceIdentities } from './unitPriceIdentity';

export interface ModelResolutionPricingSectionProps {
  /** Only rows whose condition has the resolution key. */
  value: LLMModelUnitPriceInput[];
  modelPublicId?: string;
  onChange: (next: LLMModelUnitPriceInput[]) => void;
}

const unitOptions = UNIT_PRICE_OPTIONS.filter((option) =>
  RESOLUTION_UNIT_TYPES.includes(option.unitType)
);

function resolutionOf(row: LLMModelUnitPriceInput): string {
  return String(row.condition?.[RESOLUTION_DIMENSION_KEY] ?? '');
}

export const ModelResolutionPricingSection: React.FC<ModelResolutionPricingSectionProps> = ({
  value,
  modelPublicId,
  onChange,
}) => {
  const { data: dimensions = [] } = usePricingDimensions(modelPublicId);
  const resolutionOptions = (
    dimensions.find((dimension) => dimension.key === RESOLUTION_DIMENSION_KEY)?.options ?? []
  ).map(String);
  const defaultUnit = unitOptions[0]?.unitType;
  const missingOptions = resolutionOptions.filter(
    (option) => !value.some((row) => resolutionOf(row) === option)
  );
  const duplicates = findDuplicateUnitPriceIdentities(value);

  const newRow = (resolution: string): LLMModelUnitPriceInput => ({
    unitType: (value[0]?.unitType ?? defaultUnit) as LLMModelUnitPriceType,
    priceUsd: null,
    unitScale: 1,
    condition: { [RESOLUTION_DIMENSION_KEY]: resolution },
  });

  const updateRow = (index: number, patch: Partial<LLMModelUnitPriceInput>) => {
    onChange(value.map((row, itemIndex) => (itemIndex === index ? { ...row, ...patch } : row)));
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        className="mb-1 font-semibold text-gray-700 dark:text-slate-300"
      >
        Resolution Pricing
      </Typography>
      <Typography variant="caption" className="mb-3 block text-gray-500 dark:text-slate-400">
        For models priced by output resolution (e.g. 480p / 720p video, per second). The gateway
        picks the row matching the resolution chosen in the request; rows without a resolution in
        Unit Pricing are the fallback.
      </Typography>

      <Box className="space-y-3">
        {value.map((row, index) => (
          <Box
            key={index}
            className="grid grid-cols-1 items-start gap-3 rounded border border-slate-200 p-3 dark:border-slate-700 md:grid-cols-[1.5fr_2fr_1.5fr_1fr_auto]"
          >
            <Autocomplete
              freeSolo
              options={resolutionOptions}
              value={resolutionOf(row)}
              onInputChange={(_event, input) =>
                updateRow(index, { condition: { [RESOLUTION_DIMENSION_KEY]: input.trim() } })
              }
              renderInput={(params) => (
                <TextField {...params} label="Resolution" placeholder="e.g. 720p" />
              )}
            />
            <TextField
              select
              fullWidth
              label="Unit"
              value={row.unitType}
              onChange={(event) =>
                updateRow(index, { unitType: event.target.value as LLMModelUnitPriceType })
              }
            >
              {unitOptions.map((option) => (
                <MenuItem key={option.unitType} value={option.unitType}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <FormattedNumberTextField
              fullWidth
              label="Price (USD)"
              value={row.priceUsd}
              integer={false}
              allowEmpty
              onValueChange={(priceUsd) => updateRow(index, { priceUsd: priceUsd ?? null })}
              inputProps={{ min: 0, step: 0.00000001 }}
            />
            <FormattedNumberTextField
              fullWidth
              label="Per N units"
              value={row.unitScale || 1}
              allowEmpty
              onValueChange={(unitScale) =>
                updateRow(index, { unitScale: Math.max(1, unitScale ?? 1) })
              }
              inputProps={{ min: 1, step: 1 }}
            />
            <IconButton
              className="justify-self-start md:mt-2"
              aria-label="Delete resolution price"
              onClick={() => onChange(value.filter((_row, itemIndex) => itemIndex !== index))}
            >
              <Trash2 size={18} />
            </IconButton>
          </Box>
        ))}
      </Box>

      {duplicates.length > 0 && (
        <Typography variant="caption" className="mt-2 block text-red-600">
          Duplicate resolution price rows: {duplicates.join(', ')}
        </Typography>
      )}

      <Box className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outlined"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => onChange([...value, newRow('')])}
        >
          Add resolution price
        </Button>
        {missingOptions.length > 0 && (
          <Button size="small" onClick={() => onChange([...value, ...missingOptions.map(newRow)])}>
            Add all resolutions ({missingOptions.join(', ')})
          </Button>
        )}
      </Box>
    </Box>
  );
};
